import { get } from "lodash-es";
import {
	MESApiService,
	type spParameter,
} from "../../../../services/ApiService";
import type { AuthType } from "../models/AuthType";
import { User } from "../models/User";

export const getUsers = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[SEC].[GetUsers]", [], signal);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as User[];
	} else {
		throw new Error(resp.message || "Error fetching users");
	}
};

export const getDataUser = async (
	params: { userId: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "UserId", value: params.userId },
		{ name: "UserName", value: null },
	];

	const resp = await apiService.callV2("[SEC].[GetUser]", parameters, signal);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as User[];
	} else {
		throw new Error(resp.message || "Error fetching user data");
	}
};

export const deleteUser = async (userId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "UserId", value: userId }];

	const resp = await apiService.callV2("[SEC].[DeleteUser]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when delete user");
	}
};

export const setPassword = async (userId: number, password: string) => {
	const apiService = new MESApiService();

	try {
		const resp = await apiService.changePassword(userId.toString(), password);
		return {
			ok: true,
			data: resp,
		};
	} catch (e: any) {
		return { ok: false, message: e.toString() };
	}
};

export const upsertUser = async (user: User) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "UserId", value: user.UserId },
		{ name: "UserName", value: user.UserName },
		{ name: "Name", value: user.FirstName },
		{ name: "LastName", value: user.LastName },
		{ name: "AuthTypeId", value: user.AuthTypeId },
		{ name: "IsActive", value: user.IsActive },
		{ name: "DefaultAssetId", value: user.DefaultAssetId },
	];

	const resp = await apiService.callV2("[SEC].[UpsertUser]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0].UserId", 0) as number;
	} else {
		throw new Error(resp.message || "Error when upsert user data");
	}
};

export const getAuthTypes = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[SEC].[GetAuthTypes]", [], signal);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", 0) as AuthType[];
	} else {
		throw new Error(resp.message || "Error fetching auth types");
	}
};

export const setProfilesToUser = async ({
	UserId,
	profileIds,
}: {
	UserId: number;
	profileIds: number[];
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "UserId", value: UserId },
		{ name: "ProfileIds", value: profileIds.join(" , ") },
	];

	const resp = await apiService.callV2("[SEC].[setProfilesToUser]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when set profiles to user");
	}
};

type PermissionsProp =
	| { UserId: number; UserName?: string | null }
	| { UserId?: number | null; UserName: string };

export const getUserPermissionsFromAPI = async ({
	UserId = null,
	UserName = null,
}: PermissionsProp) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "UserId", value: UserId },
		{ name: "UserName", value: UserName },
	];

	const resp = await apiService.callV2("SEC.GetPermissions", parameters);
	const rows = get(resp, "data.tables[0].rows", []) as {
		UserId: number;
		UserName: string;
		ProfilesId: string;
		Permissions: string;
		Profiles: string;
	}[];
	return rows[0] ? rows[0] : null;
};
